import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useData, isDeleted } from '../lib/data';
import { useSelection } from '../lib/selection';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Task } from '../lib/database.types';

// View for soft-deleted tasks. Accessible from the sidebar Home section.
// Lets you restore (remove the 'deleted' tag) or view the task's detail.
export function Deleted() {
  const { allTasks, projectBySlug, patchTaskLocal } = useData();
  const { select } = useSelection();
  const { push } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const deleted = useMemo(
    () =>
      allTasks
        .filter((t) => isDeleted(t))
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [allTasks],
  );

  const restore = async (task: Task) => {
    if (READONLY) return;
    setBusyId(task.id);
    const nextTags = (Array.isArray(task.tags) ? task.tags : []).filter(
      (tag) => tag !== 'deleted',
    );
    await updateTaskWithRollback({
      task,
      patch: { tags: nextTags },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ tags: nextTags })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task restored',
        body: task.title.slice(0, 60),
        ttlMs: 3000,
      },
      errorTitle: 'Failed to restore task',
    });
    setBusyId(null);
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">Trash</h1>
          <p className="text-sm text-muted font-mono mt-1">
            {deleted.length} soft-deleted task{deleted.length === 1 ? '' : 's'} · restorable
          </p>
        </header>

        {deleted.length === 0 && (
          <div className="panel p-10 text-center text-dim font-mono text-sm">
            trash is empty
          </div>
        )}

        <div className="space-y-1.5">
          {deleted.map((task) => {
            const project = projectBySlug[task.project_slug];
            return (
              <div
                key={task.id}
                className="panel p-3 flex items-center gap-3 hover:border-borderHi transition-colors"
              >
                <span className="text-red shrink-0">🗑</span>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => select({ type: 'task', id: task.id })}
                    className="text-sm font-medium text-left truncate line-through text-muted"
                  >
                    {task.title}
                  </button>
                  <div className="flex items-center gap-2 text-xs text-dim font-mono mt-0.5 flex-wrap">
                    <span>{project?.name ?? task.project_slug}</span>
                    <span>·</span>
                    <span>{task.id}</span>
                    <span>·</span>
                    <span>{new Date(task.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => restore(task)}
                  disabled={busyId === task.id}
                  className="btn text-xs shrink-0"
                >
                  {busyId === task.id ? 'restoring…' : 'restore'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
